import { Link } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";

export default function Blog() {
  useSEO({
    title: "Blog - Qicino Limited | Software Development & Tech Insights",
    description: "Read our latest articles on software development, digital transformation, cloud services, cybersecurity, and startup tips. Expert insights from Qicino Limited's team.",
    keywords: "blog, software development, digital transformation, cloud services, cybersecurity, technology news, startup tips",
    url: "https://qicino.com/blog",
  });
  const blogPosts = [
    {
      id: 1,
      title: "The Future of Software Development in Nigeria",
      excerpt:
        "Exploring the growing tech ecosystem and opportunities for software development companies in Nigeria's digital economy.",
      author: "Qicino Team",
      date: "December 15, 2024",
      category: "Industry Insights",
      image: "/images/blog-1.jpg",
      readTime: "5 min read",
    },
    {
      id: 2,
      title: "Cloud Migration Best Practices for Small Businesses",
      excerpt:
        "A comprehensive guide to successfully migrating your business operations to the cloud with minimal disruption.",
      author: "Technical Team",
      date: "December 10, 2024",
      category: "Cloud Computing",
      image: "/images/blog-2.jpg",
      readTime: "8 min read",
    },
    {
      id: 3,
      title: "UI/UX Design Trends Shaping 2025",
      excerpt:
        "Discover the latest design trends and user experience principles that will define digital products in 2025.",
      author: "Design Team",
      date: "December 5, 2024",
      category: "Design",
      image: "/images/blog-3.jpg",
      readTime: "6 min read",
    },
    {
      id: 4,
      title: "Building Scalable Mobile Applications",
      excerpt:
        "Learn the essential principles and technologies for developing mobile apps that can grow with your business needs.",
      author: "Mobile Team",
      date: "November 28, 2024",
      category: "Mobile Development",
      image: "/images/blog-4.jpg",
      readTime: "7 min read",
    },
    {
      id: 5,
      title: "Why Nigerian Businesses Need Digital Transformation",
      excerpt:
        "Understanding the importance of digital transformation for competitive advantage in the Nigerian business landscape.",
      author: "Strategy Team",
      date: "November 20, 2024",
      category: "Digital Strategy",
      image: "/images/blog-5.jpg",
      readTime: "4 min read",
    },
    {
      id: 6,
      title: "Cybersecurity Essentials for Modern Businesses",
      excerpt:
        "Protecting your business from cyber threats with essential security measures and best practices.",
      author: "Security Team",
      date: "November 15, 2024",
      category: "Security",
      image: "/images/blog-6.jpg",
      readTime: "9 min read",
    },
  ];

  const categories = [
    "All",
    "Industry Insights",
    "Cloud Computing",
    "Design",
    "Mobile Development",
    "Digital Strategy",
    "Security",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Qicino <span className="text-primary">Blog</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Insights, tutorials, and industry news from our team of software
              development experts
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === "All"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative h-64 md:h-80">
                <img
                  src={blogPosts[0].image}
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                    {blogPosts[0].category}
                  </span>
                  <span>{blogPosts[0].date}</span>
                  <span>{blogPosts[0].readTime}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {blogPosts[0].title}
                </h2>
                <p className="text-gray-700 text-lg mb-6">
                  {blogPosts[0].excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center mr-3">
                      <span className="text-white font-semibold text-sm">
                        Q
                      </span>
                    </div>
                    <span className="text-gray-700 font-medium">
                      {blogPosts[0].author}
                    </span>
                  </div>
                  <Link
                    to={`/blog/${blogPosts[0].id}`}
                    className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                  >
                    Read More
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                      {post.category}
                    </span>
                    <span className="text-xs">{post.readTime}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-2">
                        <span className="text-gray-600 font-semibold text-xs">
                          Q
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {post.author}
                        </p>
                        <p className="text-xs text-gray-600">{post.date}</p>
                      </div>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-primary hover:text-primary/80 font-medium text-sm"
                    >
                      Read →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Stay Updated with Our Latest Insights
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Subscribe to our newsletter to receive the latest articles,
              tutorials, and industry news directly in your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Subscribe
              </button>
            </form>
            <p className="text-sm text-gray-500 mt-4">
              No spam, unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Popular Tags */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Popular Tags
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Software Development",
                "Cloud Computing",
                "Mobile Apps",
                "UI/UX Design",
                "Digital Transformation",
                "Cybersecurity",
                "Nigeria Tech",
                "Startup Tips",
                "Web Development",
                "API Integration",
                "Database Design",
                "DevOps",
              ].map((tag) => (
                <span
                  key={tag}
                  className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm border border-gray-200 hover:border-primary hover:text-primary cursor-pointer transition-colors"
                >
                  #{tag.replace(/\s+/g, "")}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
